<script lang="ts" setup>
import { computed, ref } from 'vue';
import { AppTypography } from '~/components/base';
import type { Party } from '~/types/Candidates';
import { partyInitials } from '~/utils/partyInitials';
import { $pluralize } from '~/utils/pluralize';

// `eager` and `priority` are deliberately separate: eager-loading suits every
// above-the-fold card, but fetchpriority="high" is a RELATIVE hint — put it on
// six images and the browser splits early bandwidth six ways, diluting the one
// that matters. Only the LCP candidate (the first card) should claim it.
const {
  party,
  eager = false,
  priority = false,
} = defineProps<{
  party: Party;
  eager?: boolean;
  priority?: boolean;
}>();

// A 404 on the logo would otherwise show the browser's broken-image glyph
// rather than falling back to the initials below.
const logoFailed = ref(false);

const initials = computed(() => partyInitials(party));
</script>

<template>
  <NuxtLink
    :to="`/parties/${party.id}`"
    data-testid="party-card"
    class="party-card flex flex-col gap-4 no-underline"
  >
    <div class="flex items-center gap-2">
      <span
        class="party-card__avatar flex shrink-0 items-center justify-center"
      >
        <img
          v-if="party.logo && !logoFailed"
          :src="party.logo"
          :alt="party.name"
          width="72"
          height="72"
          :loading="eager ? undefined : 'lazy'"
          :fetchpriority="priority ? 'high' : undefined"
          decoding="async"
          class="party-card__avatar-image size-full object-cover"
          @error="logoFailed = true"
        />
        <AppTypography
          v-else
          type="subheadline-bold"
          class="uppercase"
          data-testid="party-card-initials"
          >{{ initials }}</AppTypography
        >
      </span>

      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <AppTypography
          type="body-bold"
          element="p"
          class="party-card__name truncate"
        >
          {{ party.name }}
        </AppTypography>
        <AppTypography
          type="subheadline"
          class="party-card__classification"
          data-testid="party-classification"
        >
          {{ party.classification === 'major' ? 'Major Party' : 'Minor Party' }}
        </AppTypography>
      </div>

      <Icon
        name="solar:alt-arrow-right-linear"
        class="party-card__chevron shrink-0"
      />
    </div>

    <div class="party-card__stats flex items-start gap-4">
      <span class="flex flex-1 items-center gap-1">
        <Icon name="solar:notes-linear" size="24" class="shrink-0" />
        <AppTypography type="title-6" class="party-card__stat-count">
          {{ party.campaignsCount }}
        </AppTypography>
        <AppTypography type="subheadline" class="party-card__stat-label">
          {{ $pluralize(party.campaignsCount, 'Campaign') }}
        </AppTypography>
      </span>
      <span class="flex flex-1 items-center gap-1">
        <Icon name="solar:buildings-linear" size="24" class="shrink-0" />
        <AppTypography type="title-6" class="party-card__stat-count">
          {{ party.lobbyistsCount }}
        </AppTypography>
        <AppTypography type="subheadline" class="party-card__stat-label">
          {{ $pluralize(party.lobbyistsCount, 'Lobbyist') }}
        </AppTypography>
      </span>
    </div>
  </NuxtLink>
</template>

<style scoped lang="scss">
.party-card {
  padding: $spacing-lg;
  // Figma: borders/default (warm) — not the cool-gray $border-color token.
  border: 1px solid #e8e3db;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;
  color: inherit;
  transition: box-shadow 0.15s ease;

  // The app-wide .router-link-active rule tints matched links with a light
  // background; this card supplies its own surface, so neutralize it.
  &.router-link-active,
  &.router-link-exact-active {
    background-color: $surface-color-bg;
  }

  &:hover {
    box-shadow: 0 2px 8px rgb(0 0 0 / 6%);
  }

  &__avatar {
    width: 72px;
    height: 72px;
    overflow: hidden;
    border: 2px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
  }

  &__avatar-image {
    border-radius: 9999px;
  }

  &__name {
    color: $text-color-brand;
  }

  &__classification {
    color: $text-color-brand-shade-1;
  }

  // Figma: text/primary (#201e1a) — $text-color-tertiary is a different
  // (navy) palette used elsewhere, so set the literal value.
  &__chevron {
    color: #201e1a;
  }

  &__stats {
    padding-top: $spacing-lg;
    // Figma: borders/default (warm) — not the cool-gray $border-color token.
    border-top: 1px solid #e8e3db;
  }

  &__stat-count {
    // Figma: text/primary (#201e1a).
    color: #201e1a;
  }

  &__stat-label {
    // Figma: text/secondary (#827b71) — this app's `.text-color-tertiary`
    // token is a different (navy) palette used elsewhere, so set the
    // literal value.
    color: #827b71;
  }
}
</style>
